/**
 * lib/teacherReach.ts
 * ------------------------------------------------------------------
 * Pure computation of which pupils (and classes) a teacher "reaches",
 * for the teacher → parent messaging feature (Part B).
 *
 * Dependency-free on purpose: the teacher-scoped composer uses it
 * client-side to constrain the audience to a teacher's own pupils, and
 * the Cloud Function mirrors the same logic server-side to RE-VALIDATE
 * a teacher's reach before resolving parents (teachers cannot read
 * `users`, so parent resolution never happens here — this resolves to
 * pupils/classes only; parents are mapped server-side).
 *
 * A teacher reaches a pupil if EITHER:
 *   • whole-class reach — they class-teach the pupil's class (class
 *     teacher or assistant, via the teacher record OR the class record),
 *     OR teach a subject to that whole class (classAssignments), OR
 *     teach a subject across the pupil's whole form (teachingAssignments
 *     → every class of that form); in these cases the ENTIRE class roster
 *     is reached; OR
 *   • per-pupil reach — the pupil's `subjectTeachers` reverse-lookup
 *     names this teacher for some subject. This reaches only that pupil,
 *     not their whole class (least privilege) — so a class the teacher
 *     only touches through individual subject links contributes just the
 *     taught pupils.
 *
 * Output:
 *   classIds   — the classes the teacher reaches AS A CLASS (the
 *                selectable "your classes" audiences).
 *   studentIds — every reachable pupil (whole-class rosters + individual
 *                subject pupils). This is the set the server validates a
 *                teacher notice/thread against.
 *   byClass    — classId → reachable pupil ids in that class, for every
 *                class with ≥1 reachable pupil (a superset of classIds:
 *                it also includes subject-only classes, with just the
 *                taught pupils). Useful for UI grouping and counts.
 * ------------------------------------------------------------------
 */

export interface ReachTeacher {
  id: string;
  /** form level -> subjectIds taught (form-level fallback). */
  teachingAssignments?: Record<string, string[]>;
  /** subjectId -> classIds taught (authoritative class-level link). */
  classAssignments?: Record<string, string[]>;
  /** classId this teacher is class teacher of. */
  classTeacherOf?: string;
  /** classIds this teacher assists on. */
  assistantClassTeacherOf?: string[];
}

export interface ReachClass {
  id: string;
  form: string;
  classTeacherId?: string;
  assistantClassTeacherId?: string;
}

export interface ReachStudent {
  id: string;
  currentClass: string; // classId
  /** subjectId -> teacherId (reverse lookup). */
  subjectTeachers?: Record<string, string>;
}

export interface TeacherReach {
  classIds: string[];
  studentIds: string[];
  byClass: Record<string, string[]>;
}

export function computeTeacherReach(
  teacher: ReachTeacher,
  classes: ReachClass[],
  students: ReachStudent[],
): TeacherReach {
  const tid = teacher.id;
  if (!tid) return { classIds: [], studentIds: [], byClass: {} };

  const classIdSet = new Set(classes.map((c) => c.id));

  // ---- 1. whole-class reach ----
  const wholeClassIds = new Set<string>();

  if (teacher.classTeacherOf) wholeClassIds.add(teacher.classTeacherOf);
  for (const id of teacher.assistantClassTeacherOf ?? []) wholeClassIds.add(id);

  // class-record back-links (the link may live on either side)
  for (const c of classes) {
    if (c.classTeacherId === tid || c.assistantClassTeacherId === tid) {
      wholeClassIds.add(c.id);
    }
  }

  // precise subject -> class assignments
  for (const ids of Object.values(teacher.classAssignments ?? {})) {
    for (const id of ids) wholeClassIds.add(id);
  }

  // form-level teaching -> every class of those forms (fallback)
  const taughtForms = new Set(
    Object.entries(teacher.teachingAssignments ?? {})
      .filter(([, subjectIds]) => (subjectIds ?? []).length > 0)
      .map(([form]) => form),
  );
  if (taughtForms.size > 0) {
    for (const c of classes) {
      if (taughtForms.has(c.form)) wholeClassIds.add(c.id);
    }
  }

  // keep only real classes
  const realWholeClassIds = new Set(
    [...wholeClassIds].filter((id) => classIdSet.has(id)),
  );

  // ---- 2. reached pupils (whole-class OR per-pupil subject link) ----
  const byClass: Record<string, string[]> = {};
  const studentIds: string[] = [];
  for (const s of students) {
    const viaClass = realWholeClassIds.has(s.currentClass);
    const viaSubject = s.subjectTeachers
      ? Object.values(s.subjectTeachers).includes(tid)
      : false;
    if (viaClass || viaSubject) {
      studentIds.push(s.id);
      (byClass[s.currentClass] ??= []).push(s.id);
    }
  }

  return {
    classIds: [...realWholeClassIds].sort(),
    studentIds,
    byClass,
  };
}

/**
 * Convenience guard for the server-side validator and the composer:
 * is `studentId` within this teacher's reach? (O(1) against a prebuilt
 * Set is cheaper for bulk checks; this is for one-off checks.)
 */
export function reaches(reach: TeacherReach, studentId: string): boolean {
  return reach.studentIds.includes(studentId);
}
